"use client";

import { useId, useState, type ChangeEvent } from "react";
import { trackEvent } from "@/lib/analytics";
import { safeSessionSet } from "@/lib/browser-storage";
import { getSEOIntentConfig, normalizeSEOIntent, type SEOIntentConfig } from "@/lib/seo-intents";
import { CONSULTATION_TOPICS } from "./_data";

/**
 * Replaces the legacy 4-chip grid that competed with the kundali form
 * for clicks. The select carries the user's topic into the form's intent
 * state via sessionStorage + a `seo-intent-refresh` event that
 * KundaliFormCard listens for. No navigation. No second CTA above the
 * fold.
 */
export default function TopicSelect({
  source = "/ai-astrologer/",
  onIntentCaptured,
}: {
  source?: string;
  onIntentCaptured?: (entry: { config: SEOIntentConfig; sourcePath: string }) => void;
}) {
  const [value, setValue] = useState("");
  const selectId = useId();

  function handleChange(e: ChangeEvent<HTMLSelectElement>) {
    const key = e.target.value;
    setValue(key);
    if (!key) return;
    const topic = CONSULTATION_TOPICS.find((t) => t.key === key);
    if (!topic) return;
    const intent = normalizeSEOIntent(topic.intent);
    const config = getSEOIntentConfig(intent);
    const capturedConfig = { ...config, prompt: topic.prompt };
    try {
      safeSessionSet("seo_entry_intent", config.intent);
      safeSessionSet("seo_entry_prompt", topic.prompt);
      safeSessionSet("seo_entry_topic", topic.key);
      safeSessionSet("seo_entry_path", source);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // sessionStorage may be disabled (private browsing); event still fires.
    }
    onIntentCaptured?.({ config: capturedConfig, sourcePath: source });
    if (typeof window !== "undefined" && !onIntentCaptured) {
      window.dispatchEvent(new CustomEvent("seo-intent-refresh"));
    }
    trackEvent("topic_select_changed", {
      intent: config.intent,
      topic_key: topic.key,
      title: topic.title,
      prompt: topic.prompt,
      source_page: source,
    });
  }

  return (
    <div className="mb-4" data-testid="ai-astrologer-topic-select">
      <label
        htmlFor={selectId}
        className="mb-1.5 block text-[12px] font-bold uppercase tracking-[0.12em]"
        style={{ color: "var(--ish-text-muted)" }}
      >
        Bring a question (optional)
      </label>
      <select
        id={selectId}
        value={value}
        onChange={handleChange}
        className="w-full rounded-lg px-3 py-2.5 text-[14px]"
        style={{
          border: "1px solid var(--ish-border)",
          background: "var(--ish-bg)",
          color: "var(--ish-dark)",
        }}
      >
        <option value="">Pick a topic for your chat</option>
        {CONSULTATION_TOPICS.map((t) => (
          <option key={t.key} value={t.key}>
            {t.title}
          </option>
        ))}
      </select>
    </div>
  );
}
